<div class="space-claim-container">
  <div class="space-claim-card">
    <app-space-claim-header 
      [title]="'Claim your event'"
      [currentStep]="currentStep === 1 ? 'Step 1 of 2' : currentStep === 2 ? 'Step 2 of 2' : null">
    </app-space-claim-header>
    
    <app-space-claim-status
      [loading]="loading"
      [hasForm]="!!eventForm"
      [isAlreadyClaimed]="isAlreadyClaimed">
    </app-space-claim-status>
    
    <!-- Step 1: Event Details -->
    <form *ngIf="!loading && eventForm && currentStep === 1" [formGroup]="eventForm" (ngSubmit)="onSubmit()" class="space-form">
      <app-wizard-header
        title="Review Event Details"
        explanation="This event is currently unclaimed. To claim it, please review the details below and complete as much information as possible. When you are done, click 'Update Event' to proceed to Step 2.">
      </app-wizard-header>

      <!--
        Bound to the live form, not to the loaded event: the organizer sees
        their edits land in the app screens as they type, which is the point of
        showing it here rather than only as a picture in the invitation email.
      -->
      <app-space-preview
        [logoUrl]="eventForm.get('logoUrl')?.value"
        [title]="eventForm.get('title')?.value"
        [description]="eventForm.get('description')?.value"
        [startDate]="eventForm.get('startDate')?.value"
        [endDate]="eventForm.get('endDate')?.value"
        [type]="eventForm.get('type')?.value"
        [brochureUrl]="eventForm.get('brochureUrl')?.value"
        [venueName]="eventForm.get('venueName')?.value"
        [locationDescription]="eventForm.get('locationDescription')?.value">
      </app-space-preview>

      <!-- Logo Upload Section -->
      <app-form-section [isLogoSection]="true">
        <div class="logo-upload-wrapper">
          <app-logo-upload
            [eventId]="eventData?._id || null"
            [currentLogoUrl]="eventForm.get('logoUrl')?.value"
            (logoUploaded)="onLogoUploaded($event)"
            (logoRemoved)="onLogoRemoved()"
          ></app-logo-upload>
        </div>
      </app-form-section>

      <!-- Name and Description Section -->
      <app-form-section title="Name and Description">
        <div class="form-row">
          <label for="title">Event name *</label>
          <input id="title" type="text" formControlName="title" maxlength="40" placeholder="Enter the event name" />
          <div class="error-message" *ngIf="getFieldError('title')">{{ getFieldError('title') }}</div>
          <div class="help-text">The name attendees will see everywhere in the app</div>
        </div>
        <div class="form-row">
          <label for="description">Full description</label>
          <textarea id="description" formControlName="description" rows="8" placeholder="Full description is unlimited and can contain any information about the event. Best used to share the event purpose, rules, invite links, speaker list, etc."></textarea>
          <div class="help-text">Full description is presented in the event page when dragging down the details handle</div>
        </div>
        <div class="form-row">
          <label for="websiteUrl">Website URL</label>
          <input id="websiteUrl" type="url" formControlName="websiteUrl" placeholder="https://example.com" />
          <div class="error-message" *ngIf="getFieldError('websiteUrl')">{{ getFieldError('websiteUrl') }}</div>
        </div>
      </app-form-section>

      <!-- Preferences Section -->
      <app-form-section title="Preferences">
        <div class="form-row">
          <label>Event Type</label>
          <div class="radio-group std-radios">
            <label class="radio-option">
              <input type="radio" formControlName="type" value="Event" />
              <span class="radio-text"><span class="radio-title">EVENT</span><span class="radio-desc">Scheduled in-person gatherings</span></span>
            </label>
            <label class="radio-option">
              <input type="radio" formControlName="type" value="Community" />
              <span class="radio-text"><span class="radio-title">COMMUNITY</span><span class="radio-desc">Ongoing online interaction</span></span>
            </label>
          </div>
        </div>
        <div class="form-row" *ngIf="eventForm.get('type')?.value === 'Event'">
          <label>Event Dates</label>
          <div class="date-group std-dates">
            <div class="date-field">
                      <label for="startDate">Start date *</label>
        <input id="startDate" type="date" formControlName="startDate" />
            </div>
            <div class="date-field">
                      <label for="endDate">End date</label>
        <div class="date-input-wrap">
          <input id="endDate" type="date" formControlName="endDate" />
          <!-- Clearing a native date input otherwise means deleting day, month
               and year one segment at a time, and an end date is optional here. -->
          <button
            type="button"
            class="date-clear"
            *ngIf="eventForm.get('endDate')?.value"
            (click)="clearEndDate()"
            aria-label="Clear end date"
            title="Clear end date"
          >&#10005;</button>
        </div>
        <div class="help-text">Leave blank for a one-day event</div>
        <div class="error-message" *ngIf="eventForm.get('endDate')?.errors?.['endDateBeforeStartDate'] && eventForm.get('endDate')?.touched">
          End date must be equal to or after the start date.
        </div>
        <app-event-too-long-notice [form]="eventForm"></app-event-too-long-notice>
            </div>
          </div>
        </div>
        <!--
          Two fields, because the app's WHERE card is two lines: the venue's name
          as the headline, its address beneath. One combined box could only ever
          fill the second, so every space claimed here printed a street line
          where the name belongs.

          Both optional. An organizer who knows only the city can still say so,
          and one who has not booked a room yet should not be blocked by a form.
        -->
        <div class="std-dates">
          <div class="date-field">
            <label for="venueName">Venue name</label>
            <input id="venueName" type="text" formControlName="venueName"
                   placeholder="e.g. Hotel Palácio Estoril" />
          </div>
          <div class="date-field">
            <label for="locationDescription">Address</label>
            <input id="locationDescription" type="text" formControlName="locationDescription"
                   placeholder="e.g. Rua Particular, Estoril" />
          </div>
        </div>
        <div class="form-row" style="margin-top: -14px">
          <div class="help-text">Shown with a map on your event page so attendees can find you</div>
        </div>
        <div class="form-row">
          <label>Registration mode</label>
          <div class="radio-group std-radios">
            <label class="radio-option">
              <input type="radio" formControlName="isPrivate" [value]="false" />
              <span class="radio-text"><span class="radio-title">PUBLIC</span><span class="radio-desc">Anyone with event link/QR can join</span></span>
            </label>
            <label class="radio-option">
              <input type="radio" formControlName="isPrivate" [value]="true" />
              <span class="radio-text"><span class="radio-title">PRIVATE</span><span class="radio-desc">Owner approves each join request</span></span>
            </label>
          </div>
        </div>
        <div class="form-row">
          <label>Lobby mode</label>
          <div class="radio-group std-radios">
            <label class="radio-option">
              <input type="radio" formControlName="lobbyMode" value="GroupChat" />
              <span class="radio-text"><span class="radio-title">GROUP CHAT</span><span class="radio-desc">Attendees talk to each other, and to you</span></span>
            </label>
            <label class="radio-option">
              <input type="radio" formControlName="lobbyMode" value="Feed" />
              <span class="radio-text"><span class="radio-title">FEED</span><span class="radio-desc">Only you post. Nobody can reply</span></span>
            </label>
          </div>
        </div>
        <div class="form-row">
          <label>Upload Agenda PDF</label>
          <app-agenda-upload
            [eventId]="eventData?._id || null"
            [currentUrl]="eventForm.get('brochureUrl')?.value"
            (agendaUploaded)="onAgendaUploaded($event)"
            (agendaRemoved)="onAgendaRemoved()"
          ></app-agenda-upload>
        </div>
      </app-form-section>

      <div class="form-actions">
        <!--
          Deliberately not disabled on an invalid form: submitting is what reveals
          the errors (onSubmit marks every control touched and scrolls to the
          first problem). A disabled button left organizers with no way to find
          out what was wrong.
        -->
        <div class="error-summary" *ngIf="submitAttempted && !eventForm.valid">
          Please fix {{ blockingFields.join(', ') }} above, then click Update Event again.
        </div>
        <button type="submit" class="save-btn" [disabled]="isUpdating">
          <span *ngIf="!isUpdating">Update Event</span>
          <span *ngIf="isUpdating">Updating...</span>
        </button>
      </div>
    </form>

    <!-- Step 2: QR Code Inline -->
    <div *ngIf="!loading && eventForm && currentStep === 2" class="qr-inline">
      <app-wizard-header
        title="Complete Claiming in NX2U App"
        explanation="Continue the claiming process on your mobile device. Scan the QR code below to be guided to the NX2U app. You will be prompted to register or sign in, and once your profile is created, you will set yourself as the owner of this event.">
      </app-wizard-header>

      <app-qr-code-display
        [isGeneratingQR]="isGeneratingQR"
        [qrCodeData]="qrCodeData"
        [deepLinkUrl]="deepLinkUrl"
        [qrError]="qrError"
        (onRetry)="generateQRCode()">
      </app-qr-code-display>

      <div class="wizard-footer">
        <button type="button" class="back-btn" (click)="goBackToStep1()">
          <span aria-hidden="true">←</span>
          <span>Back to Step 1</span>
        </button>
      </div>
    </div>

    <!--
      An organizer who arrives from a cold invitation has two reasonable
      reactions the wizard had no answer for: "what is this, actually" and "I
      have a question before I commit". Both previously dead-ended - the page
      offered claim, or nothing.

      Outside the form and the step flow on purpose, so it reads as a way out
      rather than a step, and shown on both steps: the doubt does not only
      arrive before the form.
    -->
    <div class="learn-more">
      <div class="learn-more-text">Not sure yet, or want to talk to a person first?</div>
      <div class="learn-more-actions">
        <a class="lm-btn lm-primary" href="https://nx2u.app/" target="_blank" rel="noopener">
          See what NX2U does
        </a>
        <a class="lm-btn lm-ghost" href="https://nx2u.app/#contact" target="_blank" rel="noopener">
          Ask us a question
        </a>
      </div>
    </div>
  </div>
</div>


